/**
 * CrossFamilyConversionModal Component
 * 
 * Two-step wizard for cross-family unit conversions:
 * Step 1: Enter conversion ratio
 * Step 2: Preview impact and confirm
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 12.1, 12.2, 12.3, 12.4, 12.5, 12.6, 12.7
 */

import React, { useState, useEffect, useMemo } from 'react';
import { getUnitFamily, UNIT_ID_MAP, formatQuantity } from '../utils/unitDisplay';
import http from '../services/httpClient';
import './CrossFamilyConversionModal.css';

const CrossFamilyConversionModal = ({
  isOpen,
  itemId,
  itemName,
  currentStock,
  currentUnit,
  currentUnitId,
  newUnit,
  newUnitId,
  onConfirm,
  onCancel,
  apiBaseUrl = '/api'
}) => {
  const [step, setStep] = useState(1);
  const [fromQty, setFromQty] = useState(1);
  const [fromUnit, setFromUnit] = useState(currentUnit || '');
  const [toQty, setToQty] = useState(1);
  const [toUnit, setToUnit] = useState(newUnit || '');
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showFromUnitDrop, setShowFromUnitDrop] = useState(false);
  const [showToUnitDrop, setShowToUnitDrop] = useState(false);

  const fromUnitOptions = useMemo(() => {
    const raw = String(currentUnit || '').trim();
    const normalized = getUnitFamily(raw) || getUnitFamily(UNIT_ID_MAP[currentUnitId]) || null;
    if (normalized === 'WEIGHT') {
      return [
        { value: 'kg', label: 'kg' },
        { value: 'g', label: 'g' }
      ];
    }
    if (normalized === 'VOLUME') {
      return [
        { value: 'L', label: 'L' },
        { value: 'ml', label: 'ml' }
      ];
    }
    if (normalized === 'COUNT') {
      return [
        { value: 'pcs', label: 'pcs' }
      ];
    }
    return raw ? [{ value: raw, label: raw }] : [];
  }, [currentUnit, currentUnitId]);

  const toUnitOptions = useMemo(() => {
    const raw = String(newUnit || '').trim();
    const normalized = getUnitFamily(raw) || getUnitFamily(UNIT_ID_MAP[newUnitId]) || null;
    if (normalized === 'WEIGHT') {
      return [
        { value: 'kg', label: 'kg' },
        { value: 'g', label: 'g' }
      ];
    }
    if (normalized === 'VOLUME') {
      return [
        { value: 'L', label: 'L' },
        { value: 'ml', label: 'ml' }
      ];
    }
    if (normalized === 'COUNT') {
      return [
        { value: 'pcs', label: 'pcs' }
      ];
    }
    return raw ? [{ value: raw, label: raw }] : [];
  }, [newUnit, newUnitId]);

  // Initialize units when modal opens
  useEffect(() => {
    if (isOpen) {
      setFromUnit(currentUnit || UNIT_ID_MAP[currentUnitId] || '');
      setToUnit(newUnit || UNIT_ID_MAP[newUnitId] || '');
      setStep(1);
      setError(null);
      setPreviewData(null);
      setShowFromUnitDrop(false);
      setShowToUnitDrop(false);
    }
  }, [isOpen, currentUnit, currentUnitId, newUnit, newUnitId]);

  // Pull the most specific sentence the server gave us. The route sends the real cause in
  // details.message; falling straight through to err.message (or worse, to a hardcoded
  // placeholder) is what turned every failure into an unactionable "Failed to commit
  // conversion".
  const describeError = (err, fallback) => {
    const fromDetails = err?.data?.details?.message;
    const fromBody = err?.data?.error;
    return fromDetails || fromBody || err?.message || fallback;
  };

  const handlePreview = async () => {
    setLoading(true);
    setError(null);

    try {
      // Validate itemId before making API call
      if (!itemId) {
        throw new Error('Cannot preview conversion: item not found in inventory. This ingredient may be recipe-only.');
      }
      
      const requestBody = {
        item_id: itemId,
        new_unit_id: newUnitId || parseInt(Object.keys(UNIT_ID_MAP).find(id => UNIT_ID_MAP[id] === toUnit)),
        conversion_factor: {
          fromQty: parseFloat(fromQty),
          fromUnit,
          toQty: parseFloat(toQty),
          toUnit
        }
      };
      
      console.log('📤 Sending preview conversion request:', {
        endpoint: '/inventory/preview-conversion',
        requestBody
      });
      
      const data = await http.post('/inventory/preview-conversion', requestBody);
      setPreviewData(data);
      setStep(2);
    } catch (err) {
      console.error('Preview error:', err);
      setError(describeError(err, 'Failed to preview conversion'));
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    setLoading(true);
    setError(null);

    let data;
    try {
      // Validate itemId before making API call
      if (!itemId) {
        throw new Error('Cannot commit conversion: item not found in inventory.');
      }
      
      const requestBody = {
        item_id: itemId,
        new_unit_id: newUnitId || parseInt(Object.keys(UNIT_ID_MAP).find(id => UNIT_ID_MAP[id] === toUnit)),
        conversion_factor: {
          fromQty: parseFloat(fromQty),
          fromUnit,
          toQty: parseFloat(toQty),
          toUnit
        }
      };
      
      console.log('📤 Sending commit conversion request:', {
        endpoint: '/inventory/commit-conversion',
        requestBody
      });

      data = await http.post('/inventory/commit-conversion', requestBody);
    } catch (err) {
      console.error('Commit error:', err);
      setError(describeError(err, 'Failed to commit conversion'));
      setLoading(false);
      return;
    }

    // Past this line the conversion IS committed. Notifying the parent used to sit inside the
    // try above, so a refresh/close handler that threw painted "Failed to commit conversion"
    // over a change the database had already accepted.
    setStep(1);
    setPreviewData(null);
    setError(null);
    setLoading(false);

    try {
      if (onConfirm) {
        onConfirm(data);
      }
    } catch (afterErr) {
      console.error('Post-commit handler error (conversion was saved):', afterErr);
    }
  };

  const handleBack = () => {
    setStep(1);
    setError(null);
  };

  const handleCancel = () => {
    setStep(1);
    setPreviewData(null);
    setError(null);
    if (onCancel) {
      onCancel();
    }
  };

  if (!isOpen) return null;

  const currentFamily = getUnitFamily(fromUnit);
  const newFamily = getUnitFamily(toUnit);

  return (
    <div className="cross-family-modal-overlay" onClick={(e) => e.target.className === 'cross-family-modal-overlay' && handleCancel()}>
      <div className="cross-family-modal">
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>Unit Family Conversion</h3>
          <button
            onClick={handleCancel}
            disabled={loading}
            aria-label="Close"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              background: '#f9fafb',
              color: '#6b7280',
              fontSize: 18,
              lineHeight: 1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >×</button>
        </div>

        {step === 1 && (
          <div style={{ padding: '20px', overflow: 'hidden', flex: '1 1 auto' }}>

            {/* Section label */}
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', textAlign: 'center', letterSpacing: '0.04em', marginBottom: 18 }}>
              DEFINE CONVERSION RATIO
            </div>

            {/* Conversion ratio row - full width */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 10,
              marginBottom: 16,
              boxSizing: 'border-box'
            }}>
              {/* Current side: label+input stacked, badge alongside */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-end', gap: 6 }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#111827', textAlign: 'center', display: 'block' }}>Current Unit</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={fromQty}
                    onChange={(e) => setFromQty(e.target.value)}
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '10px 8px',
                      border: '1.5px solid #d1d5db',
                      borderRadius: 9,
                      fontSize: 15,
                      textAlign: 'center',
                      outline: 'none',
                      fontWeight: 700,
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <div
                    tabIndex={0}
                    role="button"
                    aria-haspopup="listbox"
                    aria-expanded={showFromUnitDrop}
                    onClick={() => !loading && setShowFromUnitDrop(prev => !prev)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); !loading && setShowFromUnitDrop(prev => !prev); } }}
                    onBlur={() => setTimeout(() => setShowFromUnitDrop(false), 120)}
                    style={{
                      padding: '10px 24px 10px 12px',
                      background: '#f3f4f6',
                      borderRadius: 9,
                      border: '1.5px solid #e5e7eb',
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#111827',
                      whiteSpace: 'nowrap',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      userSelect: 'none',
                      position: 'relative'
                    }}
                  >
                    {fromUnit}
                    <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: 10 }}>▾</span>
                  </div>
                  {showFromUnitDrop && (
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 'calc(100% + 6px)',
                      background: '#ffffff',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: 9,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      zIndex: 1000,
                      maxHeight: 180,
                      overflowY: 'auto'
                    }}>
                      {fromUnitOptions.map(option => (
                        <button
                          key={option.value}
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); setFromUnit(option.value); setShowFromUnitDrop(false); }}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '8px 12px',
                            background: 'none',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#111827',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                          onMouseLeave={(e) => e.target.style.background = 'none'}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Equals */}
              <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', paddingBottom: 6, flexShrink: 0 }}>=</div>

              {/* New side: label+input stacked, badge alongside */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-end', gap: 6 }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#111827', textAlign: 'center', display: 'block' }}>New Unit</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={toQty}
                    onChange={(e) => setToQty(e.target.value)}
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '10px 8px',
                      border: '1.5px solid #d1d5db',
                      borderRadius: 9,
                      fontSize: 15,
                      textAlign: 'center',
                      outline: 'none',
                      fontWeight: 700,
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <div
                    tabIndex={0}
                    role="button"
                    aria-haspopup="listbox"
                    aria-expanded={showToUnitDrop}
                    onClick={() => !loading && setShowToUnitDrop(prev => !prev)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); !loading && setShowToUnitDrop(prev => !prev); } }}
                    onBlur={() => setTimeout(() => setShowToUnitDrop(false), 120)}
                    style={{
                      padding: '10px 24px 10px 12px',
                      background: '#f3f4f6',
                      borderRadius: 9,
                      border: '1.5px solid #e5e7eb',
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#111827',
                      whiteSpace: 'nowrap',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      userSelect: 'none',
                      position: 'relative'
                    }}
                  >
                    {toUnit}
                    <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: 10 }}>▾</span>
                  </div>
                  {showToUnitDrop && (
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 'calc(100% + 6px)',
                      background: '#ffffff',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: 9,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      zIndex: 1000,
                      maxHeight: 180,
                      overflowY: 'auto'
                    }}>
                      {toUnitOptions.map(option => (
                        <button
                          key={option.value}
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); setToUnit(option.value); setShowToUnitDrop(false); }}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '8px 12px',
                            background: 'none',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#111827',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                          onMouseLeave={(e) => e.target.style.background = 'none'}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {error && (
              <div style={{ marginTop: 10, padding: '9px 12px', background: '#fee2e2', borderRadius: 8, color: '#991b1b', fontSize: 13, border: '1px solid #fecaca' }}>
                {error}
              </div>
            )}
          </div>
        )}

        {step === 2 && previewData && (
          <div className="modal-body" style={{ paddingTop: '12px' }}>
            <div className="preview-section" style={{ marginBottom: '14px' }}>
              <h5 style={{ textAlign: 'center', marginTop: 0, marginBottom: '12px', fontSize: '15px', fontWeight: 700, color: '#111827' }}>Verify Conversion Impact</h5>
              
              <div className="item-preview" style={{ background: '#ffffff', border: '1px solid #e5e7eb', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                <h6 style={{ textAlign: 'left', margin: '0 0 10px 0', fontSize: '13px', fontWeight: 700, color: '#111827' }}>Item: {previewData.item.name}</h6>
                <div className="stock-comparison" style={{ display: 'flex', alignItems: 'stretch', gap: '12px', marginTop: '8px' }}>
                  <div className="stock-before" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <label style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px', fontWeight: 500, display: 'block', textAlign: 'center' }}>Current Stock</label>
                    <div className="stock-value" style={{ fontSize: '1.05rem', fontWeight: 600, color: '#111827', padding: '6px 8px', background: 'white', borderRadius: '6px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                      {formatQuantity(previewData.item.currentStock, previewData.item.currentUnit)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', paddingBottom: '8px', flexShrink: 0 }}>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#111827' }}>=</div>
                  </div>
                  <div className="stock-after" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <label style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px', fontWeight: 500, display: 'block', textAlign: 'center' }}>New Stock</label>
                    <div className="stock-value" style={{ fontSize: '1.05rem', fontWeight: 600, color: '#111827', padding: '6px 8px', background: 'white', borderRadius: '6px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                      {formatQuantity(previewData.item.newStock, previewData.item.newUnit)}
                    </div>
                  </div>
                </div>
              </div>

              {(() => {
                const rows = (list) => (
                  <div className="recipes-list">
                    {list.map((row, index) => (
                      <div key={index} className="recipe-item">
                        <div className="recipe-name">
                          <span style={{ fontWeight: 700, color: '#6b7280', marginRight: '6px' }}>{index + 1}.</span>
                          {row.recipeName || row.prepName}
                        </div>
                        <div className="recipe-conversion">
                          <span className="before">
                            {formatQuantity(row.ingredientQty, row.currentUnit)}
                          </span>
                          <span className="arrow">→</span>
                          <span className="after">
                            {formatQuantity(row.newQty, row.newUnit)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                );
                const recipes = previewData.affectedRecipes || [];
                const preparations = previewData.affectedPreparations || [];
                const complimentary = previewData.affectedComplimentary || [];
                const nothing = recipes.length === 0 && preparations.length === 0 && complimentary.length === 0;
                return (
                  <>
                    {recipes.length > 0 && (
                      <div className="recipes-preview" style={{ marginTop: '10px', paddingTop: '8px' }}>
                        <h6 style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: 700, color: '#111827' }}>Affected Recipes ({recipes.length})</h6>
                        {rows(recipes)}
                      </div>
                    )}
                    {preparations.length > 0 && (
                      <div className="recipes-preview" style={{ marginTop: '10px', paddingTop: '8px' }}>
                        <h6 style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: 700, color: '#111827' }}>Affected Preparations ({preparations.length})</h6>
                        {rows(preparations)}
                      </div>
                    )}
                    {complimentary.length > 0 && (
                      <div className="recipes-preview" style={{ marginTop: '10px', paddingTop: '8px' }}>
                        <h6 style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: 700, color: '#111827' }}>Affected Complimentary ({complimentary.length})</h6>
                        {rows(complimentary)}
                      </div>
                    )}
                    {nothing && (
                      <div className="alert alert-info" style={{ marginTop: '10px', padding: '10px 12px', fontSize: '13px' }}>
                        No recipes, preparations, or complimentary recipes use this ingredient.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {error && (
              <div className="alert alert-danger mt-3">
                {error}
              </div>
            )}
          </div>
        )}

        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          gap: 10,
          background: '#ffffff',
          borderRadius: '0 0 12px 12px',
          flexShrink: 0
        }}>
          {step === 1 && (
            <>
              <button
                onClick={handleCancel}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 10,
                  border: '1px solid #e5e7eb',
                  background: '#f3f4f6',
                  color: '#374151',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.5 : 1
                }}
              >
                Back
              </button>
              <button
                onClick={handlePreview}
                disabled={loading || !fromQty || !toQty}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 10,
                  border: 'none',
                  background: (!fromQty || !toQty || loading) ? '#9ca3af' : '#111827',
                  color: '#ffffff',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: (!fromQty || !toQty || loading) ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Saving…' : 'Save'}
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <button
                onClick={handleBack}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 10,
                  border: '1px solid #e5e7eb',
                  background: '#f3f4f6',
                  color: '#374151',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.5 : 1
                }}
              >
                Back
              </button>
              <button
                onClick={handleCommit}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 10,
                  border: 'none',
                  background: loading ? '#9ca3af' : '#111827',
                  color: '#ffffff',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Saving…' : 'Confirm'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrossFamilyConversionModal;
